import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Layers } from "lucide-react";
import { Eyebrow, MaskedLine, Reveal } from "../components/Reveal";
import { GALLERY, IMAGES } from "../data/products";

const FILTERS = [
  { key: "all", label: "All Projects" },
  { key: "residential", label: "Residential" },
  { key: "commercial", label: "Commercial" },
];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const items = filter === "all" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  return (
    <div data-testid="gallery-page">
      <section className="grain-overlay relative flex min-h-[55vh] items-end overflow-hidden" data-testid="gallery-hero">
        <img src="/images/gallery-hero.webp" alt="SOHOM floor-to-ceiling windows installation" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/30" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-40 sm:px-10">
          <Eyebrow className="mb-6">Installation Gallery — Hyderabad · Bengaluru · Goa</Eyebrow>
          <h1 className="max-w-3xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Proof, in glass</MaskedLine>
            <MaskedLine delay={0.32} className="text-gold">and profile.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:py-24">
        <div className="flex flex-wrap gap-3" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`gallery-filter-${f.key}`}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                filter === f.key
                  ? "bg-gold text-obsidian"
                  : "border border-border bg-surface text-slate-400 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-12 columns-1 gap-6 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
          <AnimatePresence>
            {items.map((g, i) => (
              <motion.button
                layout
                key={g.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                onClick={() => setActive(g)}
                className={`group relative mb-6 block w-full break-inside-avoid overflow-hidden rounded-xl border border-sky-400/20 text-left shadow-2xl ${
                  i % 3 === 1 ? "h-80" : "h-96"
                }`}
                data-testid={`gallery-item-${g.id}`}
              >
                <img src={g.image} alt={g.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-heading text-lg font-bold text-slate-50">{g.title}</p>
                  <p className="mt-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    <MapPin className="h-3 w-3 text-gold" /> {g.location}
                  </p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              className="w-full max-w-4xl overflow-hidden rounded-2xl border border-border bg-carddark shadow-2xl"
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-[50vh]">
                <img src={active.image} alt={active.title} className="h-full w-full object-cover" />
                <button onClick={() => setActive(null)} className="absolute right-4 top-4 rounded-full bg-black/60 p-2.5 text-slate-200 backdrop-blur-md transition-colors hover:bg-gold hover:text-obsidian" data-testid="lightbox-close" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4 p-7">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-slate-100">{active.title}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400">
                    <MapPin className="h-4 w-4 text-gold" /> {active.location}
                  </p>
                </div>
                <p className="flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-2 font-mono text-xs text-gold">
                  <Layers className="h-3.5 w-3.5" /> {active.systems}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Reveal className="mx-auto max-w-7xl px-6 pb-24 sm:px-10">
        <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-600">
          500+ installations delivered — every project above is a real SOHOM build
        </p>
      </Reveal>
    </div>
  );
}
